// avatar.ts — procedural cartoon people for the pond (docs/COSMETICS.md).
// One renderer, many styles: Lily is the LILY preset; member avatars (phase 2) are other AvatarStyle objects.
// Owns: drawing a person in an action (idle, walk, wave, thumbs, fish, bike, dance), the pond wanderer that picks
// activities on the bank, and the entry-screen intro (walk on, greet, walk off).
// Called by: scene.ts (wanderer + intro), avatar-lab.ts (dial-in page). No image files: everything is Graphics.

import { Container, Graphics, Text, TextStyle } from 'pixi.js';

export interface AvatarStyle {
  name: string;
  skin: string;
  blush: string;
  hair: string;
  hairShade: string;
  hairLength: 'short' | 'medium' | 'long';
  eyes: string;
  top: string;
  topStyle: 'tank' | 'tee';
  bottom: string;
  bottomShade: string;
  bottomStyle: 'shorts' | 'pants' | 'skirt';
  shoes: string;
  socks: boolean;
  watch: boolean;
  necklace: boolean;
  /** 0.85 .. 1.25: cartoon head size. */
  headScale: number;
  /** Seconds per stride; lower = brisker. */
  stride: number;
}

/**
 * Lily — from two photos the operator shared 2026-11-02 (photos are NOT stored): long golden-blonde hair with a centre
 * part, blue-grey eyes, bright smile, pink ribbed tank, light-wash denim shorts, black sandals with white socks,
 * smartwatch, fine gold necklace. Tweak on /avatar.html and paste the copied JSON here.
 */
export const LILY: AvatarStyle = {
  name: 'Lily',
  skin: '#f6d2bd',
  blush: '#f29aa6',
  hair: '#d9b27a',
  hairShade: '#b88d55',
  hairLength: 'long',
  eyes: '#7f97a8',
  top: '#ec4f9a',
  topStyle: 'tank',
  bottom: '#7fa3cc',
  bottomShade: '#c9dbef',
  bottomStyle: 'shorts',
  shoes: '#25252b',
  socks: true,
  watch: true,
  necklace: true,
  headScale: 1.1,
  stride: 0.55,
};

export const PRESETS: Record<string, AvatarStyle> = {
  Lily: LILY,
  'Pond friend': { ...LILY, name: 'Pond friend', skin: '#c98e6b', hair: '#3b2a20', hairShade: '#24180f', hairLength: 'short', eyes: '#4a3424', top: '#3fa34d', topStyle: 'tee', bottom: '#3d4f6b', bottomShade: '#56698a', bottomStyle: 'pants', shoes: '#f1f1f1', socks: false, watch: false, necklace: false, headScale: 1.0 },
  'Sunny': { ...LILY, name: 'Sunny', skin: '#8d5a3b', hair: '#1d1410', hairShade: '#0f0a08', hairLength: 'medium', eyes: '#3a2618', top: '#f6b52e', topStyle: 'tee', bottom: '#5b3f8c', bottomShade: '#7a5daf', bottomStyle: 'skirt', shoes: '#e2557b', socks: true, watch: false, necklace: true, headScale: 1.05 },
  /** Public pages (pond playground, Avatar Lab default) use this neutral host, never LILY, until Lily consents (docs/COSMETICS.md). */
  'Pond host': { ...LILY, name: 'Pond host', skin: '#e7b996', blush: '#e98b8b', hair: '#6b3f22', hairShade: '#4a2a15', hairLength: 'medium', eyes: '#3f5d3a', top: '#2f9e8f', topStyle: 'tee', bottom: '#e9c46a', bottomShade: '#f4dc9a', bottomStyle: 'shorts', shoes: '#3a5a8c', socks: false, watch: false, necklace: false, headScale: 1.05 },
};

export type Action = 'idle' | 'walk' | 'wave' | 'thumbs' | 'fish' | 'bike' | 'dance';
export const ACTIONS: Action[] = ['idle', 'walk', 'wave', 'thumbs', 'fish', 'bike', 'dance'];

const hex = (s: string) => parseInt(s.replace('#', ''), 16);
const TAU = Math.PI * 2;
type P = { x: number; y: number };
const shade = (color: number, amount = 0.72): number =>
  (Math.round(((color >> 16) & 255) * amount) << 16) | (Math.round(((color >> 8) & 255) * amount) << 8) | Math.round((color & 255) * amount);
const smooth = (v: number): number => { const k = Math.max(0, Math.min(1, v)); return k * k * (3 - 2 * k); };
type Pose = { hip: number; sway: number; bob: number; lift: number; lx: number; ly: number; rx: number; ry: number; la: number; lb: number; ra: number; rb: number; sit: number; bike: number; mouth: number };
const restPose = (): Pose => ({ hip: -47, sway: 0, bob: 0, lift: 0, lx: 0, ly: 0, rx: 0, ry: 0, la: 0.18, lb: 0.1, ra: 0.18, rb: 0.1, sit: 0, bike: 0, mouth: 0.4 });

/** Sixty per cent of a stride is a planted foot moving backwards relative to the travelling body. */
function step(phase: number): { dx: number; lift: number } {
  const p = ((phase / TAU) % 1 + 1) % 1;
  if (p < 0.6) return { dx: 18 - p * 60, lift: 0 };
  const k = (p - 0.6) / 0.4;
  return { dx: -18 + smooth(k) * 36, lift: Math.sin(k * Math.PI) * 8 };
}

/** A single cartoon person. Local origin = between the feet on the ground; +y is down, height ≈ 135 units. */
export class Avatar {
  readonly root = new Container();
  private back = new Graphics(); // props behind the body (bike, rod)
  private body = new Graphics();
  private front = new Graphics(); // props in front (line, bobber, fish, notes)
  private label: Text | null = null;
  action: Action = 'idle';
  private t = Math.random() * 10;
  private at = 0;
  private blinkIn = 2 + Math.random() * 3;
  private blinkFor = 0;
  facing: 1 | -1 = 1;
  /** Fishing: called with a LOCAL point when the bobber makes a ripple; the owner converts it. */
  onRipple: ((p: P, strength: number) => void) | null = null;
  private notes: Array<{ x: number; y: number; life: number }> = [];
  private nextBite = 4 + Math.random() * 5;
  private catchT = -1;
  /** Scene owners may opt out of continuous acting without changing the avatar's saved style. */
  reducedMotion = false;
  /** Actual travelling speed in local avatar units/second; null uses the style's preferred stride. */
  motionSpeed: number | null = null;
  private gait = 0;
  private bikeTravel = 0;
  private pose = restPose();
  private hairLag = 0;
  private feet = { lx: -8, ly: 0, rx: 8, ry: 0 };

  constructor(public style: AvatarStyle, public size = 1) {
    this.root.addChild(this.back, this.body, this.front);
    this.root.eventMode = 'none';
  }

  setAction(a: Action): void {
    if (a === this.action) return;
    this.action = a;
    this.at = 0;
    this.catchT = -1;
    this.nextBite = 4 + Math.random() * 5;
  }

  setLabel(text: string | null): void {
    if (!text) { this.label?.destroy(); this.label = null; return; }
    if (!this.label) {
      this.label = new Text({ text, style: new TextStyle({ fontFamily: 'Segoe UI, system-ui, sans-serif', fontSize: 14, fontWeight: '700', fill: 0xffffff, stroke: { color: 0x163a2b, width: 4 } }) });
      this.label.anchor.set(0.5, 1);
      this.label.position.set(0, -156);
      this.root.addChild(this.label);
    }
    this.label.text = text;
  }

  update(dt: number): void {
    dt = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;
    this.t += dt;
    this.at += dt;
    if (this.action === 'walk' && !this.reducedMotion) {
      const speed = this.motionSpeed != null && Number.isFinite(this.motionSpeed) ? this.motionSpeed : 30 / this.style.stride;
      this.gait += dt * TAU * Math.max(0, speed) / 60;
    }
    if (this.action === 'bike' && !this.reducedMotion) this.bikeTravel += dt * Math.max(0, this.motionSpeed != null && Number.isFinite(this.motionSpeed) ? this.motionSpeed : 90);
    this.blinkIn -= dt;
    if (this.blinkIn <= 0) { this.blinkFor = 0.13; this.blinkIn = 2.5 + Math.random() * 4; }
    this.blinkFor = Math.max(0, this.blinkFor - dt);
    this.root.scale.set(this.size * this.facing, this.size);
    if (this.label) this.label.scale.x = this.facing; // keep the name readable when she turns
    this.draw(dt);
  }

  // ------------------------------------------------------------------ drawing

  private draw(dt: number): void {
    const s = this.style, g = this.body, b = this.back, f = this.front;
    g.clear(); b.clear(); f.clear();
    const t = this.reducedMotion ? 0 : this.t, a = this.action, at = this.at;
    const SK = hex(s.skin), HAIR = hex(s.hair), HS = hex(s.hairShade);

    // ---- pose parameters
    let hipY = -47, sway = this.reducedMotion ? 0 : Math.sin(t * 1.25) * 0.5, lift = 0, bob = this.reducedMotion ? 0 : Math.sin(t * 2.1) * 0.65;
    let legL = { dx: 0, lift: 0 }, legR = { dx: 0, lift: 0 };
    let armL = { a: 0.18, b: 0.1 }, armR = { a: 0.18, b: 0.1 };
    let sitting = false, biking = false, wink = false, mouthOpen = 0.6;
    let handR: 'open' | 'thumb' | 'fist' = 'open';

    if (a === 'walk') {
      const ph = this.reducedMotion ? 0 : this.gait;
      legL = this.reducedMotion ? legL : step(ph);
      legR = this.reducedMotion ? legR : step(ph + Math.PI);
      armL = { a: 0.2 - Math.cos(ph) * 0.32, b: 0.28 };
      armR = { a: 0.2 + Math.cos(ph) * 0.32, b: 0.28 };
      bob = this.reducedMotion ? 0 : -Math.abs(Math.sin(ph)) * 1.8;
      sway = this.reducedMotion ? 0 : Math.cos(ph) * 0.7;
    } else if (a === 'wave') {
      const greet = this.reducedMotion ? Math.min(at, 0.9) : Math.max(0, at - 0.2);
      armR = { a: 2.55, b: Math.sin(greet * 7) * 0.45 };
      bob = this.reducedMotion ? 0 : Math.sin(t * 3) * 0.65;
      sway = this.reducedMotion ? 0 : -Math.sin(Math.min(1, at / 0.45) * Math.PI) * 1.8;
      mouthOpen = 1;
    } else if (a === 'thumbs') {
      armR = { a: 0.95, b: -2.1 };
      handR = 'thumb';
      wink = at > 0.35 && at < 1.6;
      mouthOpen = 0.8;
      bob = this.reducedMotion ? 0 : -Math.sin(Math.min(1, at / 0.65) * Math.PI) * 1.8;
    } else if (a === 'dance') {
      const ph = t * 5.4;
      sway = Math.sin(ph) * 5;
      lift = -Math.abs(Math.sin(ph)) * 7;
      armL = { a: 2.3 + Math.sin(ph) * 0.5, b: 0.3 };
      armR = { a: 2.3 - Math.sin(ph) * 0.5, b: 0.3 };
      legL = { dx: -2, lift: Math.max(0, Math.sin(ph)) * 6 };
      legR = { dx: 2, lift: Math.max(0, -Math.sin(ph)) * 6 };
      mouthOpen = 1;
      if (!this.reducedMotion && Math.random() < dt * 1.4) this.notes.push({ x: (Math.random() - 0.5) * 45, y: -142, life: 0 });
    } else if (a === 'fish') {
      sitting = true;
      hipY = -26;
      armL = { a: 0.75, b: -0.9 };
      armR = { a: 0.55, b: -1.0 };
      handR = 'fist';
      mouthOpen = 0.4;
      // Lean towards the bobber before the pull, then settle back with the catch.
      if (this.catchT >= 0 && !this.reducedMotion) {
        const pull = smooth((this.catchT - 0.6) / 0.35);
        const settle = 1 - smooth((this.catchT - 1.4) / 0.8);
        sway = (this.catchT < 0.6 ? 2 : -3.5 * pull * settle);
        armL.a += pull * settle * 0.35;
        armR.a += pull * settle * 0.3;
        mouthOpen = this.catchT > 0.7 ? 0.95 : 0.3;
      }
    } else if (a === 'bike') {
      biking = true;
      hipY = -54;
      armL = { a: 1.4, b: -0.65 };
      armR = { a: 0, b: 1.8 };
      handR = 'fist';
      mouthOpen = 0.9;
    }
    const target: Pose = { hip: hipY, sway, bob, lift, lx: legL.dx, ly: legL.lift, rx: legR.dx, ry: legR.lift, la: armL.a, lb: armL.b, ra: armR.a, rb: armR.b, sit: sitting ? 1 : 0, bike: biking ? 1 : 0, mouth: mouthOpen };
    const blend = 1 - Math.exp(-dt * (this.reducedMotion ? 18 : 11));
    const gaitBlend = a === 'walk' && !this.reducedMotion ? blend + (1 - blend) * smooth((at - 0.15) / 0.4) : blend;
    for (const key of Object.keys(target) as Array<keyof Pose>) {
      const k = key === 'lx' || key === 'ly' || key === 'rx' || key === 'ry' ? gaitBlend : blend;
      this.pose[key] += (target[key] - this.pose[key]) * k;
    }
    const pose = this.pose;
    hipY = pose.hip; sway = pose.sway; mouthOpen = pose.mouth;
    armL = { a: pose.la, b: pose.lb }; armR = { a: pose.ra, b: pose.rb };
    legL = { dx: pose.lx, lift: pose.ly }; legR = { dx: pose.rx, lift: pose.ry };
    const yo = pose.bob + pose.lift;
    this.hairLag += ((this.reducedMotion ? 0 : sway * -0.55 + Math.sin(t * 2.1 - 0.65) * 0.7 + Math.sin(this.gait - 0.8) * (a === 'walk' ? 1.6 : 0)) - this.hairLag) * (1 - Math.exp(-dt * 6));

    // ---- shadow
    g.ellipse(0, 3, 24 + pose.bike * 20, 6).fill({ color: 0x0b2a1a, alpha: 0.18 });
    g.ellipse(0, 3, 17 + pose.bike * 18, 3.5).fill({ color: 0x0b2a1a, alpha: 0.08 });

    // ---- props behind
    if (pose.sit > 0.01) b.roundRect(-21, -22, 42, 23, 10).fill({ color: 0x8a8f86, alpha: pose.sit }).roundRect(-19, -22, 38, 7, 6).fill({ color: 0xb5b9aa, alpha: pose.sit });
    let pedal: { l: P; r: P } | null = null;
    if (pose.bike > 0.01) { pedal = this.drawBike(b); b.alpha = pose.bike; } else b.alpha = 1;

    // ---- hair behind the body
    const headCx = sway * 0.6, headCy = -116 + yo + pose.sit * 22 - pose.bike * 6;
    const hr = 25.5 * s.headScale;
    const hairBack = s.hairLength === 'long' ? 50 : s.hairLength === 'medium' ? 30 : 0;
    if (hairBack) {
      const hw = hr + 4, lag = this.hairLag;
      g.moveTo(headCx, headCy - hr * 1.08)
        .quadraticCurveTo(headCx + hw * 1.1, headCy - hr, headCx + hw, headCy + 2)
        .quadraticCurveTo(headCx + hw * 0.9 + lag, headCy + hairBack * 0.35, headCx + hw + 3 + lag, headCy + hairBack * 0.58)
        .quadraticCurveTo(headCx + hw + 9 + lag, headCy + hairBack * 0.82, headCx + hw - 1 + lag, headCy + hairBack)
        .quadraticCurveTo(headCx + hw * 0.5 + lag, headCy + hairBack + 9, headCx + hw * 0.3 + lag, headCy + hairBack - 1)
        .quadraticCurveTo(headCx + lag, headCy + hairBack + 7, headCx - hw * 0.4 + lag, headCy + hairBack)
        .quadraticCurveTo(headCx - hw - 7 + lag, headCy + hairBack + 7, headCx - hw - 4 + lag, headCy + hairBack * 0.65)
        .quadraticCurveTo(headCx - hw * 0.85 + lag, headCy + hairBack * 0.4, headCx - hw, headCy + 1)
        .quadraticCurveTo(headCx - hw * 1.13, headCy - hr, headCx, headCy - hr * 1.08)
        .fill(HAIR).stroke({ color: shade(HS, 0.8), width: 1.8, alpha: 0.8 });
      for (const sx of [-1, 1]) g.moveTo(headCx + sx * hw * 0.78, headCy + 4)
        .quadraticCurveTo(headCx + sx * hw * 0.65 + lag, headCy + hairBack * 0.55, headCx + sx * hw * 0.78 + lag, headCy + hairBack - 1)
        .stroke({ color: HS, width: 3, alpha: 0.32, cap: 'round' });
    }

    // ---- legs
    const hipL: P = { x: -7 + sway, y: hipY + yo }, hipR: P = { x: 7 + sway, y: hipY + yo };
    const standingL = { x: -8 + legL.dx, y: -legL.lift }, standingR = { x: 8 + legR.dx, y: -legR.lift };
    const goalL = pedal && biking ? pedal.l : standingL, goalR = pedal && biking ? pedal.r : standingR;
    this.feet.lx += (goalL.x - this.feet.lx) * gaitBlend; this.feet.ly += (goalL.y - this.feet.ly) * gaitBlend;
    this.feet.rx += (goalR.x - this.feet.rx) * gaitBlend; this.feet.ry += (goalR.y - this.feet.ry) * gaitBlend;
    this.leg(g, hipL, { x: this.feet.lx, y: this.feet.ly }, SK, s, pose.bike > 0.5);
    this.leg(g, hipR, { x: this.feet.rx, y: this.feet.ry }, SK, s, pose.bike > 0.5);

    // ---- bottoms
    const by = hipY + yo;
    const BOT = hex(s.bottom), BSH = hex(s.bottomShade);
    if (s.bottomStyle === 'skirt') {
      g.moveTo(-17 + sway, by - 13).lineTo(17 + sway, by - 13).quadraticCurveTo(20 + sway, by, 23 + sway, by + 11)
        .quadraticCurveTo(sway, by + 17, -23 + sway, by + 11).closePath().fill(BOT).stroke({ color: shade(BOT), width: 1.5, alpha: 0.7 });
      g.moveTo(-8 + sway, by - 6).lineTo(-11 + sway, by + 9).moveTo(8 + sway, by - 6).lineTo(11 + sway, by + 9).stroke({ color: BSH, width: 1.5, alpha: 0.7 });
    } else if (!sitting && !biking && s.bottomStyle === 'shorts') {
      g.moveTo(-17 + sway, by - 13).quadraticCurveTo(sway, by - 17, 17 + sway, by - 13)
        .quadraticCurveTo(20 + sway, by - 2, 17 + sway, by + 11).quadraticCurveTo(9 + sway, by + 14, 2 + sway, by + 11)
        .lineTo(sway, by + 2).lineTo(-2 + sway, by + 11).quadraticCurveTo(-10 + sway, by + 14, -18 + sway, by + 10)
        .quadraticCurveTo(-20 + sway, by - 2, -17 + sway, by - 13).fill(BOT).stroke({ color: shade(BOT), width: 1.5, alpha: 0.75 });
      // Rounded denim cuffs and small stitched pockets keep the outfit readable at pond scale.
      g.moveTo(-17 + sway, by + 8).quadraticCurveTo(-10 + sway, by + 11, -3 + sway, by + 9)
        .moveTo(3 + sway, by + 9).quadraticCurveTo(10 + sway, by + 11, 17 + sway, by + 8).stroke({ color: BSH, width: 3.5, cap: 'round' });
      g.moveTo(-14 + sway, by - 9).quadraticCurveTo(-12 + sway, by - 1, -7 + sway, by - 2)
        .moveTo(14 + sway, by - 9).quadraticCurveTo(12 + sway, by - 1, 7 + sway, by - 2).stroke({ color: BSH, width: 1, alpha: 0.65 });
    } else {
      // pants, or shorts while sitting/biking (simple block)
      g.roundRect(-18 + sway, by - 14, 36, 18 + pose.sit * 2, 7).fill(BOT).stroke({ color: shade(BOT), width: 1.5, alpha: 0.7 });
      if (s.bottomStyle === 'shorts') g.moveTo(-14 + sway, by + (sitting ? 4 : 2)).lineTo(14 + sway, by + (sitting ? 4 : 2)).stroke({ color: BSH, width: 1.5 });
    }
    g.moveTo(-16 + sway, by - 12).quadraticCurveTo(sway, by - 9, 16 + sway, by - 12).stroke({ color: BSH, width: 2, alpha: 0.85 });

    // ---- torso / top
    const sh = by - 39; // shoulder line
    const TOP = hex(s.top);
    g.moveTo(-8 + sway, sh - 1).quadraticCurveTo(-15 + sway, sh - 2, -18 + sway, sh + 5)
      .quadraticCurveTo(-20 + sway, sh + 17, -18 + sway, by - 10).quadraticCurveTo(sway, by - 4, 18 + sway, by - 10)
      .quadraticCurveTo(20 + sway, sh + 17, 18 + sway, sh + 5).quadraticCurveTo(15 + sway, sh - 2, 8 + sway, sh - 1)
      .quadraticCurveTo(sway, sh + 4, -8 + sway, sh - 1).fill(TOP).stroke({ color: shade(TOP), width: 1.6, alpha: 0.75 });
    g.moveTo(-12 + sway, sh + 7).quadraticCurveTo(-14 + sway, sh + 18, -11 + sway, by - 14).stroke({ color: 0xffffff, width: 3, alpha: 0.15, cap: 'round' });
    if (s.topStyle === 'tank') {
      g.moveTo(-12 + sway, sh + 4).quadraticCurveTo(-13 + sway, sh - 3, -8 + sway, sh - 4)
        .moveTo(12 + sway, sh + 4).quadraticCurveTo(13 + sway, sh - 3, 8 + sway, sh - 4).stroke({ color: TOP, width: 5, cap: 'round' });
      for (let i = -10; i <= 10; i += 4) g.moveTo(i + sway, sh + 6).lineTo(i * 1.08 + sway, by - 13).stroke({ color: 0x000000, width: 0.6, alpha: 0.08 }); // ribbing
      g.ellipse(sway, sh + 1, 8, 3.5).fill(SK); // neckline
    } else {
      g.ellipse(sway, sh + 1, 6, 3).fill(SK);
    }
    if (s.necklace) {
      g.moveTo(-6 + sway, sh - 1).quadraticCurveTo(sway, sh + 7, 6 + sway, sh - 1).stroke({ color: 0xe6c36a, width: 1 });
      g.circle(sway, sh + 6, 1.4).fill(0xe6c36a);
    }
    // neck
    g.ellipse(sway * 0.8, sh - 3, 5.5, 7.5).fill(SK).stroke({ color: shade(SK, 0.8), width: 0.7, alpha: 0.5 });

    const shL = { x: -17 + sway, y: sh + 5 }, shR = { x: 17 + sway, y: sh + 5 };

    // ---- head
    const hx = headCx + sway * 0.4, hy = headCy;
    g.moveTo(hx, hy - hr).quadraticCurveTo(hx + hr * 1.05, hy - hr * 0.95, hx + hr * 0.96, hy + hr * 0.02)
      .quadraticCurveTo(hx + hr * 0.95, hy + hr * 0.86, hx + hr * 0.12, hy + hr)
      .quadraticCurveTo(hx - hr * 0.74, hy + hr * 1.04, hx - hr * 0.95, hy + hr * 0.27)
      .quadraticCurveTo(hx - hr * 1.1, hy - hr * 0.78, hx, hy - hr).fill(SK).stroke({ color: shade(SK, 0.67), width: 1.8, alpha: 0.8 });
    g.ellipse(hx + hr * 0.68, hy + hr * 0.3, hr * 0.16, hr * 0.41).fill({ color: shade(SK, 0.8), alpha: 0.12 });
    g.ellipse(hx - hr * 0.33, hy - hr * 0.2, hr * 0.44, hr * 0.52).fill({ color: 0xffffff, alpha: 0.06 });
    // ears hidden by hair for long/medium; show for short
    if (s.hairLength === 'short') g.circle(hx - hr, hy + 2, 4).fill(SK).circle(hx + hr, hy + 2, 4).fill(SK);
    // face
    const ey = hy + 1, ex = hr * 0.37;
    const gaze = this.reducedMotion ? 0 : a === 'fish' ? 1.2 : Math.sin(t * 0.7) * 0.4;
    const blink = this.blinkFor > 0;
    for (const sx of [-1, 1]) {
      const cx = hx + sx * ex;
      const closed = blink || (wink && sx === -1);
      if (closed) {
        g.moveTo(cx - 5.3, ey).quadraticCurveTo(cx, ey + 3.5, cx + 5.3, ey).stroke({ color: 0x49372f, width: 2, cap: 'round' });
      } else {
        g.ellipse(cx, ey, 5.7, 6.7).fill(0xfffef6).stroke({ color: 0x5c4438, width: 1.1, alpha: 0.7 });
        g.ellipse(cx + gaze, ey + 1, 3.8, 4.9).fill(hex(s.eyes)).stroke({ color: shade(hex(s.eyes), 0.58), width: 0.8 });
        g.ellipse(cx + gaze, ey + 1.3, 2.1, 3.3).fill(0x29282d);
        g.circle(cx + gaze + 1.3, ey - 1.5, 1.6).fill(0xffffff);
        g.circle(cx + gaze - 1.2, ey + 3, 0.7).fill({ color: 0xffffff, alpha: 0.75 });
        g.moveTo(cx - 5.2, ey - 4).quadraticCurveTo(cx, ey - 7.5, cx + 5.2, ey - 4).stroke({ color: 0x49372f, width: 1.5, cap: 'round' });
      }
      // brows
      g.moveTo(cx - 4.8, ey - 10).quadraticCurveTo(cx, ey - 12.5 - (a === 'wave' ? 1 : 0), cx + 4.8, ey - 10).stroke({ color: shade(HS, 0.85), width: 1.8, cap: 'round' });
      // blush
      g.ellipse(cx + sx * 4, ey + 9, 5.2, 3).fill({ color: hex(s.blush), alpha: 0.38 });
    }
    // nose + smile
    g.moveTo(hx - 1.5, ey + 5).quadraticCurveTo(hx, ey + 8, hx + 2.4, ey + 5.8).stroke({ color: shade(SK, 0.74), width: 1.2, alpha: 0.75, cap: 'round' });
    const mw = 7.5, my = ey + 12;
    g.moveTo(hx - mw, my).quadraticCurveTo(hx, my + 5 + mouthOpen * 4, hx + mw, my).closePath().fill(0xa74657).stroke({ color: 0x8a3f49, width: 0.7 });
    g.moveTo(hx - mw + 1.2, my + 0.4).quadraticCurveTo(hx, my + 3, hx + mw - 1.2, my + 0.4).closePath().fill(0xffffff);
    g.ellipse(hx + 1, my + 4.5 + mouthOpen, 3.3, 1.4).fill({ color: 0xef9098, alpha: 0.8 });

    // ---- hair front: a swept crown and curved locks, with no cap-like straight fringe.
    const hairOutline = shade(HS, 0.8);
    if (s.hairLength === 'short') {
      g.moveTo(hx - hr * 0.97, hy + 3).quadraticCurveTo(hx - hr * 1.1, hy - hr * 0.65, hx - hr * 0.45, hy - hr * 0.98)
        .quadraticCurveTo(hx + hr * 0.22, hy - hr * 1.24, hx + hr * 0.82, hy - hr * 0.65)
        .quadraticCurveTo(hx + hr * 1.04, hy - hr * 0.25, hx + hr * 0.94, hy + 4)
        .quadraticCurveTo(hx + hr * 0.73, hy - hr * 0.36, hx + hr * 0.42, hy - hr * 0.49)
        .quadraticCurveTo(hx - hr * 0.2, hy - hr * 0.1, hx - hr * 0.65, hy - hr * 0.24)
        .quadraticCurveTo(hx - hr * 0.75, hy + hr * 0.1, hx - hr * 0.97, hy + 3)
        .fill(HAIR).stroke({ color: hairOutline, width: 1.6, alpha: 0.8 });
      for (let i = 0; i < 8; i++) {
        const angle = Math.PI + (i / 7) * Math.PI;
        const x = hx + Math.cos(angle) * hr * 0.84, y = hy + Math.sin(angle) * hr * 0.91 - 2;
        g.ellipse(x, y, 6.5 + i % 2, 6.3).fill(HAIR).stroke({ color: hairOutline, width: 1.1, alpha: 0.65 });
        if (i % 2 === 0) g.moveTo(x - 2, y).quadraticCurveTo(x - 1, y - 4, x + 3, y - 2).stroke({ color: HS, width: 1, alpha: 0.65, cap: 'round' });
      }
    } else {
      g.moveTo(hx, hy - hr * 1.03).quadraticCurveTo(hx - hr * 0.64, hy - hr * 1.2, hx - hr * 0.98, hy - hr * 0.51)
        .quadraticCurveTo(hx - hr * 1.1, hy - hr * 0.04, hx - hr * 0.92, hy + hr * 0.23)
        .quadraticCurveTo(hx - hr * 0.81, hy - hr * 0.16, hx - hr * 0.48, hy - hr * 0.32)
        .quadraticCurveTo(hx - hr * 0.09, hy - hr * 0.46, hx, hy - hr * 1.03)
        .fill(HAIR).stroke({ color: hairOutline, width: 1.5, alpha: 0.8 });
      g.moveTo(hx, hy - hr * 1.03).quadraticCurveTo(hx + hr * 0.76, hy - hr * 1.17, hx + hr * 0.99, hy - hr * 0.42)
        .quadraticCurveTo(hx + hr * 1.08, hy + hr * 0.02, hx + hr * 0.9, hy + hr * 0.24)
        .quadraticCurveTo(hx + hr * 0.84, hy - hr * 0.18, hx + hr * 0.45, hy - hr * 0.45)
        .quadraticCurveTo(hx + hr * 0.2, hy - hr * 0.64, hx, hy - hr * 1.03)
        .fill(HAIR).stroke({ color: hairOutline, width: 1.5, alpha: 0.8 });
      const drop = s.hairLength === 'long' ? 40 : 26;
      for (const sx of [-1, 1]) {
        const lag = this.hairLag;
        g.moveTo(hx + sx * hr * 0.8, hy - hr * 0.42)
          .quadraticCurveTo(hx + sx * hr * 1.06, hy + 1, hx + sx * hr * 0.98, hy + drop * 0.35)
          .quadraticCurveTo(hx + sx * hr * 0.82 + lag * 0.5, hy + drop * 0.55, hx + sx * hr * 1.12 + lag, hy + drop * 0.76)
          .quadraticCurveTo(hx + sx * hr * 1.27 + lag, hy + drop * 1.08, hx + sx * hr * 0.76 + lag, hy + drop)
          .quadraticCurveTo(hx + sx * hr * 0.96 + lag, hy + drop * 0.8, hx + sx * hr * 0.71 + lag * 0.4, hy + drop * 0.48)
          .quadraticCurveTo(hx + sx * hr * 0.65, hy + hr * 0.13, hx + sx * hr * 0.69, hy - hr * 0.14)
          .closePath().fill(HAIR).stroke({ color: hairOutline, width: 1.3, alpha: 0.72 });
        g.moveTo(hx + sx * hr * 0.88, hy + 2).quadraticCurveTo(hx + sx * hr * 0.76 + lag * 0.4, hy + drop * 0.5, hx + sx * hr * 0.97 + lag, hy + drop * 0.87)
          .stroke({ color: HS, width: 1.6, alpha: 0.55, cap: 'round' });
      }
      g.moveTo(hx - hr * 0.77, hy - hr * 0.52).quadraticCurveTo(hx - hr * 0.54, hy - hr * 0.91, hx - hr * 0.2, hy - hr * 0.93)
        .stroke({ color: 0xfff3cb, width: 3, alpha: 0.25, cap: 'round' });
      g.moveTo(hx + hr * 0.22, hy - hr * 0.94).quadraticCurveTo(hx + hr * 0.55, hy - hr * 0.93, hx + hr * 0.77, hy - hr * 0.55)
        .stroke({ color: 0xfff3cb, width: 2, alpha: 0.14, cap: 'round' });
    }
    if (a === 'dance' || a === 'wave') {
      // a little hair bounce
      g.ellipse(hx + Math.sin(t * 9) * 2, hy - hr * 0.9, 4, 2).fill({ color: 0xffffff, alpha: 0.25 });
    }

    // ---- arms last (left = -x side, right = +x side); on the bike both reach forward to the handlebars
    const handLp = this.arm(g, shL, armL, biking ? 1 : -1, SK, s, biking ? 'fist' : 'open', false);
    const handRp = this.arm(g, shR, armR, 1, SK, s, handR, s.watch);

    // ---- props in front
    if (a === 'fish') this.drawFishing(f, handLp, handRp, dt);
    if (a === 'thumbs' && wink) f.circle(hx + ex + 10, ey - 12, 1.8).fill({ color: 0xffffff, alpha: 0.9 });
    this.drawNotes(f, dt);
  }

  private leg(g: Graphics, hip: P, foot: P, SK: number, s: AvatarStyle, onPedal: boolean): void {
    const ankle = { x: foot.x, y: foot.y - 6 };
    const knee = { x: (hip.x + ankle.x) / 2 + (onPedal ? 10 : 0), y: (hip.y + ankle.y) / 2 - (onPedal ? 6 : 0) };
    const color = s.bottomStyle === 'pants' ? hex(s.bottom) : SK;
    const width = s.bottomStyle === 'pants' ? 13 : 11.5;
    g.moveTo(hip.x, hip.y).quadraticCurveTo(knee.x + (onPedal ? 5 : 0), knee.y, ankle.x, ankle.y).stroke({ color: shade(color, 0.73), width: width + 2.2, cap: 'round', join: 'round', alpha: 0.82 });
    g.moveTo(hip.x, hip.y).quadraticCurveTo(knee.x + (onPedal ? 5 : 0), knee.y, ankle.x, ankle.y).stroke({ color, width, cap: 'round', join: 'round' });
    g.moveTo(hip.x - 2, hip.y + 5).quadraticCurveTo(knee.x - 2, knee.y, ankle.x - 2, ankle.y - 4).stroke({ color: 0xffffff, width: 2, alpha: s.bottomStyle === 'pants' ? 0.11 : 0.15, cap: 'round' });
    if (s.socks) g.moveTo(ankle.x, ankle.y - 6).lineTo(ankle.x, ankle.y + 1).stroke({ color: 0xfffdf2, width: 10, cap: 'round' });
    const SH = hex(s.shoes);
    g.moveTo(foot.x - 7, foot.y - 6).quadraticCurveTo(foot.x + 1, foot.y - 9, foot.x + 7, foot.y - 4)
      .quadraticCurveTo(foot.x + 13, foot.y - 3, foot.x + 11, foot.y + 1)
      .quadraticCurveTo(foot.x + 3, foot.y + 4, foot.x - 8, foot.y + 1)
      .quadraticCurveTo(foot.x - 10, foot.y - 1, foot.x - 7, foot.y - 6).fill(SH).stroke({ color: shade(SH, 0.7), width: 1.2 });
    g.moveTo(foot.x - 7, foot.y + 0.5).quadraticCurveTo(foot.x + 2, foot.y + 2.5, foot.x + 10, foot.y + 0.3).stroke({ color: 0xfff9e9, width: 1.7, alpha: 0.65, cap: 'round' });
    if (!onPedal) g.moveTo(foot.x - 1, foot.y - 5).lineTo(foot.x + 3, foot.y - 3).moveTo(foot.x + 2, foot.y - 6).lineTo(foot.x + 6, foot.y - 4).stroke({ color: 0xffffff, width: 1, alpha: 0.6, cap: 'round' });
  }

  /** Two-segment arm; returns the hand position. a = shoulder angle from straight down (outward +), b = elbow bend. */
  private arm(g: Graphics, sh: P, ang: { a: number; b: number }, side: number, SK: number, s: AvatarStyle, hand: 'open' | 'thumb' | 'fist', watch: boolean): P {
    const L1 = 20, L2 = 18;
    const el = { x: sh.x + Math.sin(ang.a) * L1 * side, y: sh.y + Math.cos(ang.a) * L1 };
    const hd = { x: el.x + Math.sin(ang.a + ang.b) * L2 * side, y: el.y + Math.cos(ang.a + ang.b) * L2 };
    const sleeve = s.topStyle === 'tee' ? hex(s.top) : SK;
    g.moveTo(sh.x, sh.y).quadraticCurveTo(el.x, el.y, hd.x, hd.y).stroke({ color: shade(SK, 0.7), width: 11, cap: 'round', join: 'round', alpha: 0.8 });
    g.moveTo(sh.x, sh.y).quadraticCurveTo(el.x, el.y, hd.x, hd.y).stroke({ color: SK, width: 8.8, cap: 'round', join: 'round' });
    if (s.topStyle === 'tee') {
      const cuff = { x: sh.x + (el.x - sh.x) * 0.62, y: sh.y + (el.y - sh.y) * 0.62 };
      g.moveTo(sh.x, sh.y).lineTo(cuff.x, cuff.y).stroke({ color: shade(sleeve), width: 13, cap: 'round', alpha: 0.8 });
      g.moveTo(sh.x, sh.y).lineTo(cuff.x, cuff.y).stroke({ color: sleeve, width: 11, cap: 'round' });
    }
    if (watch) {
      const w = { x: el.x + (hd.x - el.x) * 0.8, y: el.y + (hd.y - el.y) * 0.8 };
      g.circle(w.x, w.y, 3.2).fill(0xe8d7d0).circle(w.x, w.y, 1.6).fill(0x3a3a44);
    }
    g.ellipse(hd.x, hd.y + (hand === 'open' ? 0.8 : 0), hand === 'open' ? 4.8 : 5.1, hand === 'open' ? 6 : 4.9).fill(SK).stroke({ color: shade(SK, 0.75), width: 0.8, alpha: 0.7 });
    if (hand === 'thumb') g.moveTo(hd.x, hd.y - 3).lineTo(hd.x, hd.y - 9).stroke({ color: SK, width: 3.4, cap: 'round' });
    if (hand === 'open' && ang.a > 2) {
      // waving hand: spread fingers
      for (let i = -2; i <= 2; i++) g.moveTo(hd.x, hd.y).lineTo(hd.x + i * 2.2 * side, hd.y - 6).stroke({ color: SK, width: 2, cap: 'round' });
    }
    return hd;
  }

  private drawBike(b: Graphics): { l: P; r: P } {
    const wr = 15, rw = { x: -27, y: -15 }, fw = { x: 27, y: -15 }, bb = { x: -2, y: -19 };
    const spin = this.bikeTravel / wr;
    for (const w of [rw, fw]) {
      b.circle(w.x, w.y, wr).stroke({ color: 0x2b2b30, width: 3 });
      for (let i = 0; i < 4; i++) {
        const a = spin + (i * Math.PI) / 4;
        b.moveTo(w.x - Math.cos(a) * wr, w.y - Math.sin(a) * wr).lineTo(w.x + Math.cos(a) * wr, w.y + Math.sin(a) * wr).stroke({ color: 0x9aa0a6, width: 0.8 });
      }
    }
    const frame = 0xf28bb8;
    b.moveTo(rw.x, rw.y).lineTo(bb.x, bb.y).lineTo(-6, -50).lineTo(rw.x, rw.y).moveTo(bb.x, bb.y).lineTo(20, -46).lineTo(fw.x, fw.y).moveTo(-6, -50).lineTo(20, -46)
      .stroke({ color: frame, width: 3.5, cap: 'round', join: 'round' });
    b.roundRect(-14, -55, 16, 5, 2.5).fill(0x2b2b30); // saddle
    b.moveTo(20, -46).lineTo(25, -73).moveTo(14, -73).lineTo(38, -73).stroke({ color: 0x2b2b30, width: 3, cap: 'round' }); // bars meet the hands
    b.circle(29, -49, 3).fill(0xfff3a8); // lamp
    const pa = this.bikeTravel / 26;
    const pedals = { l: { x: bb.x + Math.cos(pa) * 8, y: bb.y + Math.sin(pa) * 8 }, r: { x: bb.x + Math.cos(pa + Math.PI) * 8, y: bb.y + Math.sin(pa + Math.PI) * 8 } };
    b.circle(bb.x, bb.y, 4.5).stroke({ color: 0x77818b, width: 1.5 });
    b.moveTo(pedals.l.x, pedals.l.y).lineTo(pedals.r.x, pedals.r.y).stroke({ color: 0x5d6570, width: 2 });
    return pedals;
  }

  private drawFishing(f: Graphics, hl: P, hr: P, dt: number): void {
    const grip = { x: (hl.x + hr.x) / 2, y: (hl.y + hr.y) / 2 };
    const cast = this.reducedMotion ? 1 : smooth(this.at / 0.85);
    const pull = this.reducedMotion ? 0 : smooth((this.catchT - 0.6) / 0.4) * (1 - smooth((this.catchT - 1.5) / 1));
    const tip = { x: grip.x + 20 + cast * 26 - pull * 14, y: grip.y - 30 - cast * 40 - pull * 10 };
    f.moveTo(grip.x - 6, grip.y + 6).lineTo(tip.x, tip.y).stroke({ color: 0x6b4a2b, width: 2.4, cap: 'round' });
    f.circle(grip.x - 2, grip.y + 2, 2.6).fill(0x9aa0a6); // reel
    // bite cycle
    if (!this.reducedMotion && this.at > 0.85) this.nextBite -= dt;
    let dip = 0;
    if (this.nextBite < 0 && this.catchT < 0) { this.catchT = 0; this.onRipple?.({ x: tip.x + 30, y: tip.y - 60 }, 0.8); }
    if (this.catchT >= 0) {
      const before = this.catchT;
      if (!this.reducedMotion) this.catchT += dt;
      dip = this.catchT < 0.6 ? Math.sin(this.catchT * 30) * 3 + 4 : 0;
      if (before < 2.6 && this.catchT >= 2.6) this.onRipple?.({ x: tip.x + 30, y: tip.y - 60 }, 0.6);
      if (this.catchT > 2.8) { this.catchT = -1; this.nextBite = 6 + Math.random() * 8; }
    }
    const bob = { x: tip.x + 30 * cast, y: tip.y - 60 * cast + (this.reducedMotion ? 0 : Math.sin(this.t * 2) * 1.2) + dip };
    if (this.catchT > 0.6 && this.catchT < 2.8) {
      // Notice, pull, admire, then release the fish back towards its landing ripple.
      const k = smooth((this.catchT - 0.6) / 0.8), release = smooth((this.catchT - 1.9) / 0.8);
      const fish = { x: tip.x + 30 * (1 - k) + 6 * k + release * 24, y: tip.y - 60 * (1 - k) + 18 * k - release * 78 - Math.sin(release * Math.PI) * 12 };
      f.moveTo(tip.x, tip.y).lineTo(fish.x, fish.y - 6).stroke({ color: 0xffffff, width: 0.8, alpha: 0.85 });
      const wig = this.reducedMotion ? 0 : Math.sin(this.t * 25) * 0.5;
      const alpha = 1 - smooth((this.catchT - 2.6) / 0.2);
      f.ellipse(fish.x, fish.y, 9, 4).fill({ color: 0xf0a64c, alpha });
      f.poly([fish.x - 8, fish.y, fish.x - 14, fish.y - 4 + wig * 4, fish.x - 14, fish.y + 4 + wig * 4]).fill({ color: 0xf0a64c, alpha });
      f.circle(fish.x + 5, fish.y - 1, 1).fill({ color: 0x111111, alpha });
    } else {
      f.moveTo(tip.x, tip.y).quadraticCurveTo(tip.x + 22, tip.y - 20, bob.x, bob.y).stroke({ color: 0xffffff, width: 0.8, alpha: 0.85 });
      f.circle(bob.x, bob.y, 3.2).fill(0xe94b3c);
      f.rect(bob.x - 3.2, bob.y, 6.4, 2.4).fill(0xffffff);
    }
  }

  private drawNotes(f: Graphics, dt: number): void {
    if (this.reducedMotion) { this.notes = []; return; }
    for (const n of this.notes) {
      n.life += dt;
      n.y -= dt * 28;
      n.x += Math.sin(n.life * 4) * dt * 10;
      const al = Math.max(0, 1 - n.life / 1.8);
      f.circle(n.x, n.y, 3).fill({ color: 0x163a2b, alpha: al });
      f.moveTo(n.x + 3, n.y).lineTo(n.x + 3, n.y - 11).lineTo(n.x + 8, n.y - 8).stroke({ color: 0x163a2b, width: 1.5, alpha: al });
    }
    this.notes = this.notes.filter((n) => n.life < 1.8);
  }
}

// ======================================================================== the pond wanderer

/** Lives on a straight stretch of bank (world coords), wanders and picks activities. */
export class Wanderer {
  readonly avatar: Avatar;
  private x: number;
  private target: number;
  private mode: 'walk' | 'do' | 'ride' = 'do';
  private left = 1.5;
  private rideTo = 0;
  private velocity = 0;
  private reducedGreeting = false;

  constructor(style: AvatarStyle, private xMin: number, private xMax: number, private y: number, size: number, private ripple: (x: number, y: number, s: number) => void) {
    this.avatar = new Avatar(style, size);
    this.x = xMin + (xMax - xMin) * 0.3;
    this.target = this.x;
    this.avatar.root.position.set(this.x, y);
    this.avatar.onRipple = (p, s) => this.ripple(this.x + p.x * size * this.avatar.facing, this.y + p.y * size, s);
  }

  /** Make her greet now (used when the intro hands over). */
  greet(): void {
    if (this.avatar.reducedMotion && this.reducedGreeting) return;
    this.mode = 'do';
    this.velocity = 0;
    this.avatar.setAction(Math.random() < 0.5 ? 'wave' : 'thumbs');
    this.left = 2.4;
  }

  update(dt: number): void {
    dt = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;
    const a = this.avatar;
    if (a.reducedMotion) {
      if (!this.reducedGreeting) { this.reducedGreeting = true; this.left = 1.2; a.setAction('wave'); }
      this.left -= dt;
      if (this.left <= 0) a.setAction('idle');
      this.velocity = 0;
      a.motionSpeed = 0;
      a.root.position.set(this.x, this.y);
      a.update(dt);
      return;
    }
    if (this.reducedGreeting) { this.mode = 'do'; this.left = 2; a.setAction('idle'); }
    this.reducedGreeting = false;
    const previousX = this.x;
    if (this.mode === 'walk' || this.mode === 'ride') {
      const speed = this.mode === 'ride' ? 150 : 46 * (0.55 / a.style.stride);
      const dir = Math.sign(this.target - this.x);
      a.facing = dir >= 0 ? 1 : -1;
      const braking = Math.max(0.16, Math.min(1, Math.abs(this.target - this.x) / 45));
      this.velocity += (dir * speed * braking - this.velocity) * (1 - Math.exp(-dt * 8));
      this.x += this.velocity * dt;
      if ((dir > 0 && this.x >= this.target) || (dir < 0 && this.x <= this.target) || Math.abs(this.x - this.target) < 1) {
        this.x = this.target;
        this.velocity = 0;
        this.pickActivity();
      }
    } else {
      this.left -= dt;
      if (this.left <= 0) this.pickNextMove();
    }
    a.root.position.set(this.x, this.y);
    a.motionSpeed = dt > 0 ? Math.abs(this.x - previousX) / (dt * Math.max(0.1, a.size)) : 0;
    a.update(dt);
  }

  private pickNextMove(): void {
    const r = Math.random();
    if (r < 0.2) {
      this.mode = 'ride';
      this.rideTo = this.x < (this.xMin + this.xMax) / 2 ? this.xMax - 40 : this.xMin + 40;
      this.target = this.rideTo;
      this.avatar.setAction('bike');
    } else {
      this.mode = 'walk';
      this.target = this.xMin + Math.random() * (this.xMax - this.xMin);
      this.avatar.setAction('walk');
    }
  }

  private pickActivity(): void {
    this.mode = 'do';
    const r = Math.random();
    const [act, secs]: [Action, number] = r < 0.38 ? ['fish', 10 + Math.random() * 10] : r < 0.6 ? ['dance', 4 + Math.random() * 3] : r < 0.75 ? ['wave', 2.4] : r < 0.85 ? ['thumbs', 2.2] : ['idle', 2 + Math.random() * 3];
    if (act === 'fish') this.avatar.facing = 1;
    this.avatar.setAction(act);
    this.left = secs;
  }
}

// ======================================================================== the entry-screen intro

/** Screen-space: walks on from the left, greets, waits, walks off. `walkOff()` may be called early (e.g. on sign-in). */
export class Intro {
  readonly avatar: Avatar;
  private phase: 'in' | 'greet' | 'wait' | 'out' | 'done' = 'in';
  private x: number;
  private left = 0;
  private velocity = 0;
  done = false;

  constructor(style: AvatarStyle, private stopX: () => number, private groundY: () => number, size: () => number, private onDone: () => void) {
    this.avatar = new Avatar(style, size());
    this.x = -90;
    this.avatar.setAction('walk');
    this.sizeFn = size;
  }
  private sizeFn: () => number;

  walkOff(): void {
    if (this.phase === 'out' || this.phase === 'done') return;
    this.phase = 'out';
    this.avatar.setAction('walk');
  }

  update(dt: number, screenW: number): void {
    if (this.done) return;
    dt = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;
    const a = this.avatar;
    a.size = this.sizeFn();
    const previousX = this.x;
    const speed = 110 * (a.size / 1.6) * (0.55 / a.style.stride);
    if (a.reducedMotion) {
      this.velocity = 0;
      if (this.phase === 'in') { this.x = this.stopX(); this.phase = 'greet'; a.setAction('wave'); this.left = 1.2; }
      else if (this.phase === 'greet') { this.left -= dt; if (this.left <= 0) { this.phase = 'wait'; a.setAction('idle'); this.left = 5; } }
      else if (this.phase === 'wait') { this.left -= dt; if (this.left <= 0) this.walkOff(); }
      else if (this.phase === 'out') {
        a.root.alpha = Math.max(0, a.root.alpha - dt * 5);
        if (a.root.alpha === 0) { this.phase = 'done'; this.done = true; this.onDone(); }
      }
      a.root.position.set(this.x, this.groundY());
      a.motionSpeed = 0;
      a.update(dt);
      return;
    }
    a.root.alpha = 1;
    if (this.phase === 'in') {
      a.facing = 1;
      const braking = Math.max(0.18, Math.min(1, (this.stopX() - this.x) / 80));
      this.velocity += (speed * braking - this.velocity) * (1 - Math.exp(-dt * 8));
      this.x += this.velocity * dt;
      if (this.x >= this.stopX() - 1) { this.x = this.stopX(); this.velocity = 0; this.phase = 'greet'; a.setAction(Math.random() < 0.5 ? 'wave' : 'thumbs'); this.left = 2.6; }
    } else if (this.phase === 'greet') {
      this.left -= dt;
      if (this.left <= 0) { this.phase = 'wait'; a.setAction('idle'); this.left = 5; }
    } else if (this.phase === 'wait') {
      this.left -= dt;
      if (this.left <= 0) this.walkOff();
    } else if (this.phase === 'out') {
      a.facing = -1;
      this.velocity += (-speed - this.velocity) * (1 - Math.exp(-dt * 8));
      this.x += this.velocity * dt;
      if (this.x < -120) { this.phase = 'done'; this.done = true; this.onDone(); }
    }
    a.root.position.set(this.x, this.groundY());
    a.motionSpeed = dt > 0 ? Math.abs(this.x - previousX) / (dt * Math.max(0.1, a.size)) : 0;
    a.update(dt);
    void screenW;
  }
}

export function sanitizeStyle(raw: unknown, base: AvatarStyle = LILY): AvatarStyle {
  const out: AvatarStyle = { ...base };
  if (!raw || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;
  const color = (v: unknown) => (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v : null);
  for (const k of ['skin', 'blush', 'hair', 'hairShade', 'eyes', 'top', 'bottom', 'bottomShade', 'shoes'] as const) { const c = color(r[k]); if (c) out[k] = c; }
  if (r.hairLength === 'short' || r.hairLength === 'medium' || r.hairLength === 'long') out.hairLength = r.hairLength;
  if (r.topStyle === 'tank' || r.topStyle === 'tee') out.topStyle = r.topStyle;
  if (r.bottomStyle === 'shorts' || r.bottomStyle === 'pants' || r.bottomStyle === 'skirt') out.bottomStyle = r.bottomStyle;
  for (const k of ['socks', 'watch', 'necklace'] as const) if (typeof r[k] === 'boolean') out[k] = r[k] as boolean;
  if (typeof r.headScale === 'number' && Number.isFinite(r.headScale)) out.headScale = Math.min(1.25, Math.max(0.85, r.headScale));
  if (typeof r.stride === 'number' && Number.isFinite(r.stride)) out.stride = Math.min(1, Math.max(0.3, r.stride));
  if (typeof r.name === 'string') out.name = r.name.slice(0, 24);
  return out;
}
